<template>
  <Loading :loading="isFetching" loading-text="Loading..." background="none-background">
    <div class="document-templates-archive">
      <div class="content-wrapper" :class="{ 'loading': isFetching }">
        <div class="templates-content" :class="{ 'loading': isFetching }">
          <div class="templates-grid" :class="{ 'is-empty': !isFetching && items.length === 0 }">
            <div v-if="!isFetching && items.length === 0" class="no-data-state">
              <img src="@/assets/MyWorkBench/archive/empty.svg" alt="Empty" class="empty-icon" />
              <p class="empty-text">You don't have any archived document templates.</p>
            </div>

            <Loading
              v-for="item in items"
              :key="item.id"
              :loading="processingItemId === item.id"
              :loading-text="''"
            >
              <div class="template-card-shell">
                <WorkbenchCard
                  class="template-card"
                  :title="item.name"
                  :description="item.description || '-'"
                  :notes="`Archived · ${formatDate(item.updated_at)}`"
                  :show-preview="false"
                  :show-edit="false"
                  :show-archive="false"
                >
                  <template #icon>
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="24" viewBox="0 0 19 24" fill="none">
                      <path d="M4.75 13.2H14.25V10.8H4.75V13.2ZM4.75 16.8H14.25V14.4H4.75V16.8ZM4.75 20.4H10.6875V18H4.75V20.4ZM2.375 24C1.72187 24 1.16276 23.765 0.697656 23.295C0.232552 22.825 0 22.26 0 21.6V2.4C0 1.74 0.232552 1.175 0.697656 0.705C1.16276 0.235 1.72187 0 2.375 0H11.875L19 7.2V21.6C19 22.26 18.7674 22.825 18.3023 23.295C17.8372 23.765 17.2781 24 16.625 24H2.375ZM10.6875 8.4V2.4H2.375V21.6H16.625V8.4H10.6875Z" fill="currentColor"/>
                    </svg>
                  </template>

                  <template #actions>
                    <div v-if="processingItemId !== item.id" class="action-buttons">
                      <button class="action-btn restore-btn" @click.stop="handleRestore(item)">
                        <img src="@/assets/MyWorkBench/archive/restore.svg" alt="Restore" class="action-icon" />
                        <span class="action-text">Restore</span>
                      </button>

                      <button class="action-btn delete-permanently-btn" @click.stop="handleDelete(item)">
                        <img src="@/assets/MyWorkBench/archive/delete.svg" alt="Delete" class="action-icon" />
                        <span class="action-text">Permanently Delete</span>
                      </button>
                    </div>
                  </template>
                </WorkbenchCard>
              </div>
            </Loading>
          </div>
        </div>

        <!-- Pagination at the bottom -->
        <Pagination
          v-if="items.length > 0"
          :total_items="total"
          :current_page="currentPage"
          :per_page="perPage"
          :per_page_options="[10, 20, 30]"
          @update:current_page="handlePageChange"
          @update:per_page="handlePageSizeChange"
        />
      </div>


      <DeleteConfirmDialog
        v-model="deleteDialogVisible"
        title="Delete Document Template"
        :item-name="selectedTemplate?.name"
        :confirm-action="handleTemplateDelete"
        loading-text="Deleting document template..."
        modal-class="delete-confirm-dialog"
      />
    </div>
  </Loading>
</template>

<script>
import Loading from '@/NewLibrary/Loading/Loading.vue';
import WorkbenchCard from '@/views/commonComponents/WorkbenchCard.vue';
import DeleteConfirmDialog from '@/views/my-workbench-new/archive/components/DeleteConfirmDialog.vue';
import { notify } from '@/utils/notify';
import {
  getArchivedFormsApi,
  restoreFormApi,
  deleteFormPermanentlyApi
} from '@/api/archive';

export default {
  name: 'DocumentTemplatesArchive',
  components: {
    Loading,
    WorkbenchCard,
    DeleteConfirmDialog,
  },

  data() {
    return {
      isFetching: false,
      perPage: 10,
      currentPage: 1,
      items: [],
      total: 0,
      deleteDialogVisible: false,
      selectedTemplate: null,
      processingItemId: null,
    };
  },

  watch: {
    perPage() {
      this.fetchArchivedForms();
    },
    currentPage() {
      this.fetchArchivedForms();
    },
  },

  mounted() {
    this.fetchArchivedForms();
  },

  methods: {
    async fetchArchivedForms(showLoading = true) {
      if (showLoading) this.isFetching = true;
      try {
        const offset = (this.currentPage - 1) * this.perPage;
        const response = await getArchivedFormsApi({
          limit: this.perPage,
          offset: offset,
        });
        this.items = response.data.items || [];
        this.total = response.data.total || 0;
      } catch (error) {
        console.error('Failed to fetch archived forms:', error);
        this.items = [];
        this.total = 0;
      } finally {
        if (showLoading) this.isFetching = false;
      }
    },

    async handleRestore(item) {
      if (this.processingItemId) return;
      try {
        this.processingItemId = item.id;
        await restoreFormApi(item.id);
        notify({ type: 'success', message: `"${item.name}" has been restored` });
        await this.fetchArchivedForms(false);
      } catch (error) {
        console.error('Failed to restore form:', error);
        notify({ type: 'error', message: `Failed to restore "${item.name}"` });
      } finally {
        this.processingItemId = null;
      }
    },

    handleDelete(item) {
      this.selectedTemplate = item;
      this.deleteDialogVisible = true;
    },

    async handleTemplateDelete() {
      try {
        await deleteFormPermanentlyApi(this.selectedTemplate.id);
        notify({
          type: 'success',
          title: 'Document Template Deleted',
          message: `"${this.selectedTemplate.name}" has been permanently deleted.`
        });
        await this.fetchArchivedForms(false);
      } catch (error) {
        console.error('Failed to delete form:', error);
        notify({
          type: 'error',
          title: 'Error',
          message: 'Failed to delete document template. Please try again.'
        });
        throw error;
      } finally {
        this.selectedTemplate = null;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    handlePageChange(page) {
      this.currentPage = page;
    },

    handlePageSizeChange(size) {
      this.perPage = size;
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.document-templates-archive {
  padding: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  padding: 24px 24px 24px 0;
  min-height: 0;
}

.content-wrapper.loading {
  opacity: 0.3;
  pointer-events: none;
}

.templates-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0;
  max-height: 100%;
}

.templates-content::-webkit-scrollbar {
  width: 6px;
}

.templates-content::-webkit-scrollbar-track {
  background: transparent;
}

.templates-content::-webkit-scrollbar-thumb {
  background: #C4C4C4;
  border-radius: 3px;
}

.templates-content::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.templates-content.loading {
  opacity: 0.3;
  pointer-events: none;
}

.templates-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  flex: 1;
  align-content: start;
  min-height: 100%;
}

.templates-grid.is-empty {
  align-content: stretch;
}

.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  grid-column: 1 / -1;
  min-height: 100%;
  text-align: center;
}

.empty-icon {
  width: 350px;
  height: 230px;
}

.empty-text {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #333;
  margin: 0;
}

.template-card-shell {
  width: 100%;
  min-width: 0;
}

.template-card {
  min-height: 144px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: flex-end;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 6px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: 24px;
}

.action-icon {
  flex-shrink: 0;
  display: block;
}

.action-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  max-width: 0;
  overflow: hidden;
  opacity: 0;
  transition: all 0.2s ease;
}

.restore-btn:hover {
  background: rgba(0, 72, 121, 0.08);
}

.restore-btn:hover .action-icon {
  filter: grayscale(1) brightness(0.5);
}

.restore-btn:hover .action-text {
  max-width: 180px;
  overflow: visible;
  opacity: 1;
  color: #1E4670;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

.delete-permanently-btn:hover {
  background: #FFEBEE;
  color: #D10000;
}

.delete-permanently-btn:hover .action-icon {
  filter: brightness(1.1);
}

.delete-permanently-btn:hover .action-text {
  max-width: 180px;
  opacity: 1;
  color: #D10000;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

.template-card :deep(.card-notes) {
  color: #999;
}
</style>
